import { Injectable } from '@angular/core';
import { SupportAttachmentDraft } from './support-attachment.models';

export const SUPPORT_IMAGE_MAX_WIDTH = 1920;
export const SUPPORT_IMAGE_MAX_BYTES = 1_572_864;
export const SUPPORT_MAX_ATTACHMENTS = 3;
/** Plafond over de hele melding (melding + alle reacties); spiegel van FeedbackMaxAttachmentsPerTicket in de backend. */
export const SUPPORT_MAX_ATTACHMENTS_PER_TICKET = 12;

const JPEG_QUALITY = 0.85;
/** Aantal keer dat de breedte gehalveerd wordt als de jpeg boven de bytegrens blijft. */
const MAX_SHRINK_ITERATIONS = 3;
/** Jpeg kent geen alpha: transparante delen worden anders zwart. */
const JPEG_BACKGROUND = '#ffffff';

let fallbackIdCounter = 0;

/** `crypto.randomUUID` bestaat alleen in een secure context; daarbuiten volstaat een lokaal uniek id. */
export function newAttachmentId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  fallbackIdCounter++;
  return `${Date.now().toString(36)}-${fallbackIdCounter}`;
}

/**
 * Brengt een afbeelding (screenshot, plak, upload) naar het formaat dat de API
 * verwacht: jpeg, maximaal 1920 breed en onder de bytegrens. Zuiver canvas-werk,
 * zonder DOM-afhankelijkheden buiten `createImageBitmap` en een offscreen canvas.
 */
@Injectable({ providedIn: 'root' })
export class SupportImageService {
  /** Overschrijfbaar in specs om het tooLarge-pad af te dwingen. */
  protected maxBytes = SUPPORT_IMAGE_MAX_BYTES;

  async normalize(blob: Blob, fileName: string): Promise<SupportAttachmentDraft> {
    const bitmap = await createImageBitmap(blob);
    try {
      let width = Math.min(bitmap.width, SUPPORT_IMAGE_MAX_WIDTH);
      for (let attempt = 0; attempt <= MAX_SHRINK_ITERATIONS; attempt++) {
        const height = Math.max(1, Math.round((bitmap.height * width) / bitmap.width));
        const result = await this.encode(bitmap, width, height);
        if (result.size <= this.maxBytes) {
          return { id: newAttachmentId(), blob: result, previewUrl: URL.createObjectURL(result), width, height, fileName };
        }
        width = Math.max(1, Math.floor(width / 2));
      }
    } finally {
      bitmap.close();
    }
    throw new Error('tooLarge');
  }

  private encode(bitmap: ImageBitmap, width: number, height: number): Promise<Blob> {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) return Promise.reject(new Error('canvas'));
    context.fillStyle = JPEG_BACKGROUND;
    context.fillRect(0, 0, width, height);
    context.drawImage(bitmap, 0, 0, width, height);
    return new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(result => (result ? resolve(result) : reject(new Error('encode'))), 'image/jpeg', JPEG_QUALITY);
    });
  }
}
